import { postRequestOtpRegister, postRequestRegister } from "@/api/user/home";
import useMaskedCardNumber from "@/hooks/useMaskedCardNumber";
import OtpInput from "@/shared/components/common/otp";
import CustomButton from "@/shared/components/kit/button";
import StackKit from "@/shared/components/kit/stack";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import { zodResolver } from "@hookform/resolvers/zod";
import { CircularProgress } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import React, { Dispatch, SetStateAction } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { z } from "zod";
import { toPersianNumber } from "@/hooks/useThousandsSeparator";

const formSchema = z.object({otp: z.string().min(1, "کد تایید الزامی است").length(6, "کد تایید باید 6 رقم باشد").regex(/^\d{6}$/, "کد تایید باید شامل 6 رقم باشد"),});

type FormData = z.infer<typeof formSchema>;
/**
 * OtpRegister
 *
 * کامپوننت مربوط به ثبت‌نام کاربر و تایید OTP.
 * این کامپوننت شامل فرم ورود کد OTP ارسالی به شماره موبایل کاربر می‌باشد
 * و با استفاده از react-hook-form و Zod، اعتبارسنجی OTP انجام می‌شود.
 *
 * ویژگی‌ها:
 * - نمایش شماره موبایل به صورت ماسک شده با استفاده از useMaskedCardNumber
 * - مدیریت زمان‌بندی ارسال مجدد OTP با شمارش معکوس
 * - امکان ارسال دوباره کد با کلیک روی دکمه "ارسال مجدد کد"
 * - ارسال درخواست ثبت‌نام و تایید OTP به سرور با استفاده از react-query
 * - نمایش loader هنگام ارسال درخواست
 * - مدیریت جریان ثبت‌نام و OTP با استفاده از stateهای setIsRegister، setIsOtpRegister و setIsForgetPassword
 *
 * Props:
 * @param setIsRegister - تابع برای مدیریت نمایش فرم ثبت‌نام
 * @param setIsOtpRegister - تابع برای مدیریت نمایش فرم تایید OTP
 * @param setIsForgetPassword - تابع برای مدیریت نمایش فرم فراموشی رمز عبور
 * @param phoneNumberRegister - شماره موبایل کاربر که OTP به آن ارسال شده
 * @param nidRegister - شماره ملی کاربر
 * @param passRegister - رمز عبور انتخاب شده کاربر
 * @param resetPassRegister - تکرار رمز عبور برای تایید
 *
 * @returns JSX.Element
 */
interface OtpRegisterProps{
  setIsRegister: Dispatch<SetStateAction<boolean>>;
  setIsOtpRegister: Dispatch<SetStateAction<boolean>>;
  setIsForgetPassword: Dispatch<SetStateAction<boolean>>;
  phoneNumberRegister: any;
  nidRegister: any;
  passRegister: string,
  resetPassRegister: string
}
const OtpRegister = ({
  setIsRegister,
  setIsOtpRegister,
  setIsForgetPassword,
  phoneNumberRegister,
  nidRegister,
  passRegister,
  resetPassRegister
}:OtpRegisterProps) => {

  const { handleSubmit, control } = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });

  const [timeLeft, setTimeLeft] = React.useState(120);

  React.useEffect(() => {
    if (timeLeft <= 0) return;
    const interval = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [timeLeft]);

  const formatTime = (seconds: number) => {
    const min = Math.floor(seconds / 60).toString().padStart(1, "0");
    const sec = (seconds % 60).toString().padStart(2, "0");
    return `${min}:${sec}`;
  };

  const { mutate: fetchRequestRegister, } = useMutation({
    mutationFn: postRequestRegister,
    onSuccess: (data: any) => {
      setIsRegister(false);
      setIsOtpRegister(true);
    },
    onError: (error: AxiosError<{ message: string }>) => {
      toast.error(error?.response?.data?.message);
    },
  });
  
  const { mutate: fetchRequestOtpRegister, isPending } = useMutation({
    mutationFn: postRequestOtpRegister,
    onSuccess: (data: any) => {
      setIsRegister(false);
      setIsOtpRegister(false);
      setIsForgetPassword(false);
    },
    onError: (error: AxiosError<{ message: string }>) => {
      toast.error(error?.response?.data?.message);
    },
  });

  const onSubmit = async (data: FormData) => {
    const requestBody = { nid: nidRegister, otp: data?.otp };
    fetchRequestOtpRegister(requestBody);
  };
  
  const onSubmitReset = async (data: FormData) => {

    const requestBody = {
      code: null,
      mobile: phoneNumberRegister,
      nID: nidRegister,
      pass: passRegister,
      passRepeat: resetPassRegister,
    };
    fetchRequestRegister(requestBody);
  };

  const maskedCar = useMaskedCardNumber;
  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{ width: "100%" }}>
      <StackKit justifyContent={"center"} alignItems={"center"} gap={2} sx={{ width: { xs: '100%', sm: '100%', md: '80%' }, }}>
        <TypoGraphyKit variant="body1">
          کد امنیتی پیامک شده به شماره موبایل
          {toPersianNumber(maskedCar(phoneNumberRegister))} را وارد نمایید
        </TypoGraphyKit>
        <button type="button" style={{ background: "none", border: "none", color: "#3687FF", cursor: "pointer", padding: 0, textDecoration: "underline", }} onClick={() => (setIsOtpRegister(false), setIsRegister(true))}>
          ویرایش شماره موبایل
        </button>
        <Controller name="otp" control={control} 
          render={({ field, fieldState }) => (
            <>
              <OtpInput value={field.value} onChange={field.onChange} onComplete={(val) => field.onChange(val)} hasResend onResend={onSubmitReset}/>
              {fieldState.error && (
                <TypoGraphyKit variant="body2" sx={{ color: "#ff6b6b", fontSize: "12px" }}>
                  {fieldState.error.message}
                </TypoGraphyKit>
              )}
            </>
          )}
        />
        <StackKit sx={{width: "40%",}} alignItems={"center"}>
          <CustomButton type="submit" sx={{background: "linear-gradient(90deg, #F4D7D3 21.62%, #78B9C1 87.47%) !important",width: "100%",}}>
            <TypoGraphyKit variant="body1" sx={{display: "flex",justifyContent: "center",alignItems: "center",}}>
              {isPending ? (
                <CircularProgress sx={{ color: "#fff", width: "20px", height: "20px" }} size={22}/>
              ) : (
                "ادامه"
              )}
            </TypoGraphyKit>
          </CustomButton>
        </StackKit>
      </StackKit>
    </form>
  );
};

export default OtpRegister;
